/**
 * Countdown timer custom element
 * Displays a dynamic countdown with days, hours, minutes, and seconds until a specified end date
 */

export class CountdownElement extends HTMLElement {
  private timer: number | null = null
  private endTime: number = 0
  private daysElement: HTMLElement | null = null
  private hoursElement: HTMLElement | null = null
  private minutesElement: HTMLElement | null = null
  private secondsElement: HTMLElement | null = null
  private lastDays: string = ''
  private lastHours: string = ''
  private lastMinutes: string = ''
  private lastSeconds: string = ''
  private isVisible: boolean = false

  constructor() {
    super()
    this.updateCountdown = this.updateCountdown.bind(this)
  }

  connectedCallback() {
    this.daysElement = this.querySelector('[data-countdown-days]')
    this.hoursElement = this.querySelector('[data-countdown-hours]')
    this.minutesElement = this.querySelector('[data-countdown-minutes]')
    this.secondsElement = this.querySelector('[data-countdown-seconds]')

    const endDate = this.getAttribute('data-end-date')
    if (endDate) {
      this.endTime = new Date(endDate).getTime()
      const now = new Date().getTime()
      const timeDiff = this.endTime - now

      if (timeDiff > 0) {
        this.updateCountdown()
        this.timer = window.setInterval(this.updateCountdown, 1000)
      } else {
        this.setAttribute('data-ui', 'hidden')
      }
    }
  }

  disconnectedCallback() {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }
  }

  updateCountdown() {
    const now = new Date().getTime()
    const timeDiff = this.endTime - now

    if (timeDiff < 1) {
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      if (this.isVisible) {
        this.isVisible = false
        requestAnimationFrame(() => {
          this.setAttribute('data-ui', 'hidden')
        })
      }
      return
    }

    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000)

    const daysStr = days.toString().padStart(2, '0')
    const hoursStr = hours.toString().padStart(2, '0')
    const minutesStr = minutes.toString().padStart(2, '0')
    const secondsStr = seconds.toString().padStart(2, '0')

    const needsUpdate = daysStr !== this.lastDays ||
                        hoursStr !== this.lastHours ||
                        minutesStr !== this.lastMinutes ||
                        secondsStr !== this.lastSeconds ||
                        !this.isVisible

    if (needsUpdate) {
      this.lastDays = daysStr
      this.lastHours = hoursStr
      this.lastMinutes = minutesStr
      this.lastSeconds = secondsStr

      requestAnimationFrame(() => {
        if (this.daysElement) {
          this.daysElement.textContent = daysStr
        }
        if (this.hoursElement) {
          this.hoursElement.textContent = hoursStr
        }
        if (this.minutesElement) {
          this.minutesElement.textContent = minutesStr
        }
        if (this.secondsElement) {
          this.secondsElement.textContent = secondsStr
        }
        if (!this.isVisible) {
          this.isVisible = true
          this.setAttribute('data-ui', 'visible')
        }
      })
    }
  }
}
